import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Camera, Clock3, FileText, MapPin, RefreshCw, Upload, X, IndianRupee, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { uploadFile } from '@/lib/uploads';
import { openRazorpayCheckout } from '@/lib/razorpay';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';

const statusText = {
  quoting: 'Comparing nearby labs',
  payment_pending: 'Best quotation ready',
  payment_failed: 'Payment failed',
  confirmed: 'Booking confirmed',
  collector_en_route: 'Collector on the way',
  collector_arrived: 'Collector arrived',
  sample_collected: 'Sample collected',
  processing: 'Processing your sample',
  report_ready: 'Report ready',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_lab_available: 'No nearby lab available',
};

export default function ConsumerLabTests() {
  const cameraRef = useState(null);
  const [sheet, setSheet] = useState(null);
  const [mode, setMode] = useState(null);
  const [text, setText] = useState('');
  const [rxId, setRxId] = useState('');
  const [rxName, setRxName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationState, setLocationState] = useState('locating');
  const [order, setOrder] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [searching, setSearching] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [fileInput, setFileInput] = useState(null);

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Location is not available'));
    setLocationState('locating');
    navigator.geolocation.getCurrentPosition(
      (p) => { const x={latitude:p.coords.latitude,longitude:p.coords.longitude}; setLocation(x); setLocationState('ready'); resolve(x); },
      () => { setLocationState('denied'); reject(new Error('Allow location access so we can find nearby labs')); },
      { enableHighAccuracy:true, timeout:12000, maximumAge:30000 }
    );
  });

  const loadHistory = async () => { try { const {data}=await api.get('/lab/orders'); setHistory(data.orders||[]); } catch {} };
  useEffect(() => { loadHistory(); getLocation().catch(()=>{}); }, []);

  useEffect(() => {
    if (!order?.id) return;
    const t=setInterval(async()=>{
      try {
        const detail=await api.get('/lab/orders/'+order.id);
        setOrder(detail.data.order);
        if (['quoting'].includes(detail.data.order?.status)) {
          const q=await api.get('/lab/orders/'+order.id+'/quotes');
          setQuotes(q.data.quotes||[]);
        } else if (detail.data.order?.status==='payment_pending') {
          const q=await api.get('/lab/orders/'+order.id+'/quotes');
          setQuotes(q.data.quotes||[]);
        }
      } catch {}
    },3000);
    return ()=>clearInterval(t);
  },[order?.id]);

  const handleUpload=async(e)=>{
    const file=e.target.files?.[0]; if(!file)return;
    const isImage=file.type?.startsWith('image/');
    if(!isImage && file.size>6*1024*1024)return toast.error('PDF must be 6MB or smaller');
    if(isImage && file.size>15*1024*1024)return toast.error('Photo must be 15MB or smaller');
    setUploading(true);
    try{
      const url=await uploadFile('prescriptions',file);
      const {data}=await api.post('/users/me/prescriptions',{doctor_name:'Not provided',title:'Lab request',image_url:url});
      setRxId(data.prescription.id);setRxName(file.name);setSheet(null);toast.success('Prescription ready');
    }catch(e){toast.error(e.response?.data?.detail||e.message||'Upload failed')}
    finally{setUploading(false);e.target.value=''}
  };

  const submit=async()=>{
    if(mode==='prescription'&&!rxId)return toast.error('Upload your prescription first');
    if(mode==='text'&&text.trim().length<3)return toast.error('Tell us what you need');
    setSearching(true);
    try{
      const gps=location||await getLocation();
      const payload={latitude:gps.latitude,longitude:gps.longitude,request_text:mode==='text'?text.trim():undefined,prescription_id:mode==='prescription'?rxId:undefined};
      const {data}=await api.post('/lab/orders',payload);
      setOrder(data.order);setQuotes([]);setMode(null);setText('');setRxId('');setRxName('');await loadHistory();
      toast.success('Your request is with nearby labs');
    }catch(e){toast.error(e.response?.data?.detail||e.message||'Could not send request')}
    finally{setSearching(false)}
  };

  const alternative=async()=>{
    try{const {data}=await api.post('/lab/orders/'+order.id+'/request-alternative');setOrder(data.order);setQuotes([]);toast.success('We are asking more nearby labs')}
    catch(e){toast.error(e.response?.data?.detail||'Could not request another quote')}
  };

  const pay=async()=>{
    try{
      const {data}=await api.post('/lab/orders/'+order.id+'/payment/order');
      await openRazorpayCheckout({
        checkout:data.checkout,description:'RESQLY Lab booking',
        onSuccess:async(resp)=>{
          try{
            await api.post('/lab/orders/payment/verify',{order_id:order.id,razorpay_order_id:resp.razorpay_order_id,razorpay_payment_id:resp.razorpay_payment_id,razorpay_signature:resp.razorpay_signature});
            const x=await api.get('/lab/orders/'+order.id);setOrder(x.data.order);toast.success('Lab booking confirmed');
          }catch(e){toast.error(e.response?.data?.detail||'Payment verification failed')}
        },
        onDismiss:()=>toast.message('Payment window closed'),
        onFailed:()=>toast.error('Payment failed — retry when ready')
      });
    }catch(e){toast.error(e.response?.data?.detail||'Payment could not start')}
  };

  const reset=()=>{setOrder(null);setQuotes([]);setMode(null);setRxId('');setRxName('');setText('');loadHistory()};

  return (
    <MobileShell title="Labs">
      <div className="min-h-screen bg-[#f7f7f5] px-5 py-4 pb-28">
        <button onClick={()=>window.history.back()} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back</button>

        {!order && (
          <>
            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="mt-4 rounded-[30px] p-6 bg-gradient-to-br from-violet-700 via-violet-600 to-fuchsia-500 text-white">
              <div className="text-[10px] uppercase tracking-[.22em] text-white/70 font-bold">RESQLY LABS</div>
              <div className="text-3xl font-black mt-2">No medical jargon. Just tell us what you need.</div>
              <p className="text-sm text-white/80 mt-2">Send a prescription or type the test you are looking for. Nearby labs quote you.</p>
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/15 text-xs"><MapPin className="w-3.5 h-3.5"/>{locationState==='ready'?'Using your current location':'Finding your location…'}</div>
            </motion.div>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <button onClick={()=>{setMode('prescription');setSheet('upload')}} className="rounded-3xl bg-white border p-5 text-left shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center"><FileText/></div>
                <div className="font-black mt-3">Upload prescription</div>
                <div className="text-xs text-slate-500 mt-1">Camera or files</div>
              </button>
              <button onClick={()=>setMode('text')} className="rounded-3xl bg-white border p-5 text-left shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-violet-50 text-violet-700 flex items-center justify-center"><Sparkles/></div>
                <div className="font-black mt-3">Tell us what you need</div>
                <div className="text-xs text-slate-500 mt-1">Type it in plain language</div>
              </button>
            </div>

            {mode==='text'&&(
              <div className="mt-4 rounded-3xl bg-white border p-5">
                <div className="font-black">What are you looking for?</div>
                <textarea rows={5} value={text} onChange={e=>setText(e.target.value)} className="w-full mt-3 rounded-2xl border p-3 text-sm" placeholder="For example: full body blood test, thyroid test, urine test…"/>
                <Button className="w-full mt-3 bg-violet-700" onClick={submit} disabled={searching}>{searching?'Finding labs…':'Send to nearby labs'}</Button>
              </div>
            )}

            {rxName&&(
              <div className="mt-4 rounded-3xl bg-white border p-4 flex items-center gap-3">
                <FileText className="text-emerald-600"/>
                <div className="flex-1"><div className="font-bold">{rxName}</div><div className="text-xs text-slate-500">Prescription ready</div></div>
                <Button size="sm" className="bg-violet-700" onClick={submit} disabled={searching}>{searching?'Sending…':'Send'}</Button>
              </div>
            )}

            {history.length>0&&(
              <div className="mt-7"><div className="font-black mb-3">Recent requests</div><div className="space-y-2">{history.slice(0,5).map(x=><div key={x.id} className="rounded-2xl bg-white border p-3 flex justify-between gap-3"><span className="text-sm font-semibold">#{x.id.slice(0,8)}</span><span className="text-xs text-slate-500">{statusText[x.status]||x.status}</span></div>)}</div></div>
            )}
          </>
        )}

        {order&&(
          <div className="mt-4 rounded-[28px] bg-white border shadow-sm p-5">
            <div className="flex items-start justify-between">
              <div><div className="text-[10px] uppercase tracking-widest text-violet-600 font-bold">Your request</div><div className="font-black text-xl mt-1">{statusText[order.status]||order.status}</div></div>
              <button onClick={async()=>{const q=await api.get('/lab/orders/'+order.id+'/quotes');setQuotes(q.data.quotes||[])}} className="p-2 rounded-full bg-slate-100"><RefreshCw className="w-4 h-4"/></button>
            </div>

            <div className="mt-3 text-xs text-slate-500">{order.request_type==='prescription'?'Prescription uploaded':order.request_text}</div>
            {order.status==='quoting'&&(
              <div className="mt-4">
                <div className="rounded-2xl bg-slate-50 border p-4">
                  <div className="font-bold">Comparing nearby labs</div>
                  <div className="text-xs text-slate-500 mt-1">Lowest quotation wins. If two labs quote the same price, the lab with the faster backend-calculated ETA wins.</div>
                  {quotes[0]&&<div className="mt-3 rounded-2xl bg-white border p-3"><div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Current best</div><div className="font-black mt-1">{quotes[0].provider_name||'Verified Lab'}</div><div className="grid grid-cols-2 gap-2 mt-3"><Mini icon={IndianRupee} value={'₹'+Number(quotes[0].amount||0).toFixed(0)} label="Quotation"/><Mini icon={Clock3} value={(quotes[0].eta_minutes||'—')+' min'} label="Backend ETA"/></div></div>}
                  <div className="mt-3 text-xs text-slate-400">We keep comparing until the quote window closes or all nearby labs have responded.</div>
                </div>
                <button onClick={alternative} className="w-full mt-3 rounded-2xl border py-3 text-sm font-bold">Ask for another group of labs</button>
              </div>
            )}

            {order.status==='payment_pending'&&(
              <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                <div className="font-bold text-emerald-900">Best quotation selected</div>
                <div className="text-sm text-emerald-800 mt-1">₹{Number(order.pricing_snapshot?.total||0).toFixed(0)} · {order.pricing_snapshot?.eta_minutes||'—'} min ETA</div>
                <div className="text-xs text-emerald-800/70 mt-1">Selected by lowest quotation, then ETA.</div>
                <Button className="w-full mt-3 bg-emerald-700" onClick={pay}>Pay & book</Button>
              </div>
            )}

            {order.status==='payment_failed'&&<Button onClick={pay} className="w-full mt-4 bg-rose-600">Retry payment</Button>}
            {order.status==='no_lab_available'&&<div className="mt-4 rounded-2xl bg-amber-50 border border-amber-100 p-4"><div className="font-bold text-amber-900">No nearby lab could quote this request.</div><Button className="w-full mt-3 bg-amber-600" onClick={alternative}>Try nearby labs again</Button></div>}
            {['confirmed','collector_en_route','collector_arrived','sample_collected','processing','report_ready','completed'].includes(order.status)&&<StatusCard order={order}/>}
            {order.report_url&&<button onClick={()=>setReportOpen(true)} className="mt-3 w-full rounded-2xl border py-3 font-bold flex items-center justify-center gap-2"><FileText className="w-4 h-4"/> View report</button>}
            {['completed','cancelled'].includes(order.status)&&<Button className="w-full mt-4" onClick={reset}>Make another request</Button>}
          </div>
        )}

        {sheet==='upload'&&(
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={()=>setSheet(null)}>
            <div className="w-full max-w-[520px] bg-white rounded-t-[28px] p-5 space-y-3" onClick={e=>e.stopPropagation()}>
              <div className="flex justify-between items-center"><div className="font-black text-lg">Add prescription</div><button onClick={()=>setSheet(null)}><X/></button></div>
              <input ref={el=>el&&(window.__labCameraInput=el)} type="file" accept="image/*" capture="environment" onChange={handleUpload} className="hidden"/>
              <input ref={el=>setFileInput(el)} type="file" accept="image/*,application/pdf" onChange={handleUpload} className="hidden"/>
              <button onClick={()=>window.__labCameraInput?.click()} disabled={uploading} className="w-full rounded-2xl border p-4 flex items-center gap-3 text-left"><Camera className="text-blue-600"/><div><div className="font-bold">Camera</div><div className="text-xs text-slate-500">Take a clear photo</div></div></button>
              <button onClick={()=>fileInput?.click()} disabled={uploading} className="w-full rounded-2xl border p-4 flex items-center gap-3 text-left"><Upload className="text-violet-600"/><div><div className="font-bold">Files & Photos</div><div className="text-xs text-slate-500">Choose a photo or PDF</div></div></button>
              {uploading&&<div className="text-xs text-slate-500 text-center">Preparing and uploading securely…</div>}
            </div>
          </div>
        )}

        {reportOpen&&order?.report_url&&<div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={()=>setReportOpen(false)}><iframe src={order.report_url} title="Lab report" className="w-full h-full max-w-5xl rounded-2xl bg-white"/><button className="absolute top-4 right-4 text-white" onClick={()=>setReportOpen(false)}><X/></button></div>}
      </div>
    </MobileShell>
  );
}

function Mini({icon:Icon,value,label}) {
  return <div className="rounded-2xl bg-white border p-3"><Icon className="w-3.5 h-3.5 text-slate-400"/><div className="font-black text-sm mt-1">{value}</div><div className="text-[10px] text-slate-400">{label}</div></div>;
}
function StatusCard({order}) {
  const steps=['confirmed','collector_en_route','collector_arrived','sample_collected','processing','report_ready'];
  const idx=Math.max(0,steps.indexOf(order.status));
  return <div className="mt-4 rounded-3xl bg-slate-950 text-white p-4"><div className="flex justify-between text-xs text-white/60"><span>Lab booking</span><span className="text-violet-300 font-bold">{statusText[order.status]}</span></div><div className="mt-4 flex gap-1">{steps.map((s,i)=><div key={s} className={'h-1.5 flex-1 rounded-full '+(i<=idx?'bg-violet-400':'bg-white/10')}/>)}</div></div>;
}
